import React from 'react';
import { AlertCircle, CheckCircle2, Circle } from 'lucide-react';
import { StatusListData, StatusListState } from '../../types';
import { cn } from '../../../../lib/utils';

interface RequirementStatusViewProps {
    data: StatusListData;
}

const stateToTitleClass = (state: StatusListState) => {
    if (state === 'success') return 'text-foreground';
    if (state === 'warning') return 'text-foreground';
    return 'text-foreground';
};

const stateToItemClass = (state?: StatusListState) => {
    if (state === 'success') return 'text-emerald-700 dark:text-emerald-200';
    if (state === 'warning') return 'text-amber-700 dark:text-amber-200';
    return 'text-muted-foreground dark:text-[#C8CDD3]';
};

const stateToSurfaceClass = (state: StatusListState) => {
    if (state === 'success') {
        return 'border-emerald-500/30 bg-emerald-500/[0.08]';
    }
    if (state === 'warning') {
        return 'border-amber-500/30 bg-amber-500/[0.08]';
    }
    return 'border-border/45 bg-background/45 dark:border-white/10 dark:bg-[#10161F]/80';
};

const stateToIconClass = (state: StatusListState) => {
    if (state === 'success') return 'text-emerald-600 dark:text-emerald-300';
    if (state === 'warning') return 'text-amber-600 dark:text-amber-300';
    return 'text-muted-foreground dark:text-[#A9AFB8]';
};

const iconByName = (iconName?: string, state?: StatusListState) => {
    const normalized = iconName?.toLowerCase();
    if (normalized === 'check' || normalized === 'check-circle') {
        return CheckCircle2;
    }
    if (normalized === 'warning' || normalized === 'alert') {
        return AlertCircle;
    }
    if (state === 'success') return CheckCircle2;
    if (state === 'warning') return AlertCircle;
    return Circle;
};

export const RequirementStatusView: React.FC<RequirementStatusViewProps> = ({ data }) => {
    const TitleIcon = iconByName(data.icon, data.state);
    const items = Array.isArray(data.items) ? data.items : [];

    return (
        <div
            className={cn(
                'space-y-3 rounded-xl border p-4 shadow-sm',
                stateToSurfaceClass(data.state)
            )}
        >
            <div className="flex items-center gap-3">
                <TitleIcon className={cn('h-5 w-5', stateToIconClass(data.state))} />
                <span className={`font-semibold text-xl leading-tight ${stateToTitleClass(data.state)}`}>
                    {data.title}
                </span>
            </div>
            {items.length > 0 && (
                <ul className="space-y-1.5">
                    {items.map((item) => (
                        <li key={item.id} className="flex items-start gap-2">
                            {React.createElement(
                                iconByName(item.icon, item.state || data.state),
                                {
                                    className: `mt-0.5 h-4 w-4 ${stateToItemClass(
                                        item.state || data.state
                                    )}`,
                                }
                            )}
                            <span
                                className={`text-base leading-6 ${stateToItemClass(
                                    item.state || data.state
                                )}`}
                            >
                                {item.label}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
